// Base Field - Abstract base class for all field models
import {ref} from "valtio";

import {ValidateInterface} from "@/shared/contracts/validate.interface";
import {EventEmitter} from "@/shared/event-emitter";

import {FormFieldEvents, ValidationError} from "./types";
import {BaseValidator, RequiredValidator} from "./validators";

export interface BaseFieldConfig {
    validators?: Array<BaseValidator>;
    disabled?: boolean;
}

export abstract class BaseField implements ValidateInterface {
    // Reactive state properties (shared)
    _disabled: boolean;
    _errors: Array<ValidationError>;

    // Non-reactive properties (wrapped in ref)
    protected readonly validators: Array<BaseValidator>;
    public readonly events: EventEmitter<FormFieldEvents>;

    protected constructor(config: BaseFieldConfig = {}) {
        this._disabled = config.disabled || false;
        this._errors = [];

        this.validators = ref(config.validators || []) as Array<BaseValidator>;
        this.events = ref(new EventEmitter<FormFieldEvents>()) as EventEmitter<FormFieldEvents>;
    }

    // Common getters
    get disabled(): boolean {
        return this._disabled;
    }

    get error(): ValidationError | null {
        return this._errors[0] || null;
    }

    get errors(): Array<ValidationError> {
        return this._errors;
    }

    get hasError(): boolean {
        return this._errors.length > 0;
    }

    get shouldShowError(): boolean {
        return this.hasError;
    }

    get isRequired(): boolean {
        return this.validators.some((validator) => validator instanceof RequiredValidator);
    }

    get requireMessage(): string {
        const requiredValidator = this.validators.find((v) => v instanceof RequiredValidator);
        return requiredValidator?.message || "required";
    }

    get isValid(): boolean {
        return this.validate().isValid;
    }

    // Common setters
    setDisabled(disabled: boolean): void {
        this._disabled = disabled;
    }

    // Common event handlers
    onBlur(): void {
        this.validate();
        this.events.emit("blur");
    }

    onFocus(): void {
        this.events.emit("focus");
    }

    scrollIntoView(): void {
        this.events.emit("scrollIntoView");
    }

    // Lifecycle

    // Helper method for running custom validators
    protected runValidators(value: string): {isValid: boolean; error?: string} {
        for (const validator of this.validators) {
            const result = validator.validate(value);
            if (!result.isValid) {
                const error: ValidationError = {
                    type: "error",
                    message: result.error || "validationError",
                    messageParams: result.errorParams,
                };
                this._errors = [error];
                return {isValid: false, error: result.error};
            }
        }
        return {isValid: true};
    }

    // Abstract methods that must be implemented by subclasses
    abstract validate(): {isValid: boolean; error?: string};
    abstract reset(): void;
    abstract clear(): void;
}
